import { supabase } from '@/integrations/supabase/client';

export interface ClaimSharedQuoteResult {
  success: boolean;
  claimed: boolean;
  alreadyOwner: boolean;
  eventId?: string | null;
  eventCode?: string | null;
  error?: string | null;
  message?: string | null;
}

/**
 * Transfere a posse de uma proposta compartilhada para a sessão atual.
 *
 * Propostas abertas por link são inicialmente vinculadas a um usuário anônimo.
 * Ao reabrir o link em outro aparelho/aba (ou após a sessão anônima expirar),
 * o novo uid não consegue ler o evento por causa da RLS. Esta chamada pede à
 * edge function `claim-shared-quote` que reatribua o evento (pai e filhos) ao
 * usuário atual — apenas quando o dono anterior é nulo ou anônimo.
 *
 * Nunca lança: retorna `success: false` para o chamador decidir o fallback.
 */
export async function claimSharedQuote(
  params: { eventId?: string | null; eventCode?: string | null; verifyPhone?: string | null },
): Promise<ClaimSharedQuoteResult> {
  const eventId = params.eventId?.trim() || undefined;
  const eventCode = params.eventCode?.trim() || undefined;

  if (!eventId && !eventCode) {
    return { success: false, claimed: false, alreadyOwner: false, error: 'MISSING_IDENTIFIER' };
  }

  try {
    const { data, error } = await supabase.functions.invoke('claim-shared-quote', {
      body: { eventId, eventCode, ...(params.verifyPhone != null ? { verifyPhone: params.verifyPhone } : {}) },
    });

    if (error) {
      console.warn('[claimSharedQuote] invoke error:', error.message);
      // Respostas 4xx do servidor trazem o motivo no corpo (ex.: VERIFICATION_FAILED).
      try {
        const body = await (error as any).context?.json?.();
        if (body?.error) {
          return { success: false, claimed: false, alreadyOwner: false, error: body.error, message: body.message ?? null };
        }
      } catch { /* sem corpo legível */ }
      return { success: false, claimed: false, alreadyOwner: false, error: error.message };
    }

    const payload = (data ?? {}) as Partial<ClaimSharedQuoteResult>;

    return {
      success: !!payload.success,
      claimed: !!payload.claimed,
      alreadyOwner: !!payload.alreadyOwner,
      eventId: payload.eventId ?? null,
      eventCode: payload.eventCode ?? null,
      error: payload.error ?? null,
      message: payload.message ?? null,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    console.warn('[claimSharedQuote] unexpected error:', message);
    return { success: false, claimed: false, alreadyOwner: false, error: message };
  }
}
